import { getEasing } from './Easing';
import { lerp, interpolateColor, now, requestFrame, cancelFrame } from '../utils';
import { getPathLength, getPointAtLength, morphPath } from '../utils/pathGeometry';
var nextId = 1;
var activeAnimations = [];
var rafScheduled = false;
var pendingFrameId = 0;
function isColorProp(key) {
    return key === 'fill' || key === 'stroke' || key === 'color' || key === 'background';
}
function applyProps(target, props) {
    for (var key in props) {
        target[key] = props[key];
    }
    if (typeof target.markDirty === 'function') {
        target.markDirty();
    }
}
function interpolateProps(from, to, t) {
    var result = {};
    for (var key in to) {
        var a = from[key];
        var b = to[key];
        if (typeof a === 'number' && typeof b === 'number') {
            result[key] = lerp(a, b, t);
        }
        else if (typeof a === 'string' && typeof b === 'string' && isColorProp(key)) {
            result[key] = interpolateColor(a, b, t);
        }
        else {
            result[key] = t < 1 ? a : b;
        }
    }
    return result;
}
function resolveMotionPath(motionPath) {
    if (typeof motionPath === 'string')
        return motionPath;
    if (motionPath && typeof motionPath === 'object' && 'd' in motionPath) {
        return motionPath.d;
    }
    return null;
}
function cancelPendingTick() {
    if (rafScheduled && pendingFrameId) {
        cancelFrame(pendingFrameId);
        rafScheduled = false;
        pendingFrameId = 0;
    }
}
function tick() {
    var _a, _b, _c;
    rafScheduled = false;
    pendingFrameId = 0;
    var currentTime = now();
    var needsRedraw = false;
    for (var i = activeAnimations.length - 1; i >= 0; i--) {
        var anim = activeAnimations[i];
        if (!anim.active)
            continue;
        var elapsed = currentTime - anim.startTime - anim.delay;
        if (elapsed < 0)
            continue;
        var progress = anim.duration > 0 ? elapsed / anim.duration : 1;
        if (progress >= 1) {
            if (anim.loop || anim.iteration < anim.repeat) {
                anim.iteration++;
                if (anim.reverse)
                    anim.reversed = !anim.reversed;
                anim.startTime = currentTime;
                progress = 0;
            }
            else {
                progress = 1;
            }
        }
        var eased = anim.easing(clampProgress(progress, anim.reversed));
        var from = anim.reversed ? anim.to : anim.from;
        var to = anim.reversed ? anim.from : anim.to;
        var current = interpolateProps(from, to, eased);
        if (anim.motionPathD) {
            var pt = getPointAtLength(anim.motionPathD, eased * anim.pathLength);
            current.x = pt.x;
            current.y = pt.y;
            if ('rotation' in anim.to || 'rotation' in anim.from) {
                current.rotation = pt.angle;
            }
        }
        if (anim.morphFrom && anim.morphTo) {
            current.d = morphPath(anim.morphFrom, anim.morphTo, eased);
        }
        applyProps(anim.target, current);
        (_a = anim.onUpdate) === null || _a === void 0 ? void 0 : _a.call(anim, eased);
        needsRedraw = true;
        if (progress >= 1 && anim.iteration >= anim.repeat && !anim.loop) {
            (_b = anim.onComplete) === null || _b === void 0 ? void 0 : _b.call(anim);
            anim.active = false;
            activeAnimations.splice(i, 1);
        }
    }
    if (activeAnimations.some(function (a) { return a.active; })) {
        scheduleTick();
    }
    else {
        cancelPendingTick();
    }
    if (needsRedraw) {
        (_c = AnimationEngine.onFrame) === null || _c === void 0 ? void 0 : _c.call(AnimationEngine);
    }
}
function clampProgress(t, reversed) {
    return reversed ? 1 - t : t;
}
function scheduleTick() {
    if (!rafScheduled) {
        rafScheduled = true;
        pendingFrameId = requestFrame(tick);
    }
}
var AnimationEngine = /** @class */ (function () {
    function AnimationEngine() {
    }
    /** Test hook: whether a RAF callback is scheduled. */
    AnimationEngine.isTickScheduled = function () {
        return rafScheduled;
    };
    AnimationEngine.animate = function (target, options) {
        var _a, _b, _c, _d, _e, _f, _g;
        var duration = (_a = options.duration) !== null && _a !== void 0 ? _a : 300;
        var delay = (_b = options.delay) !== null && _b !== void 0 ? _b : 0;
        var easing = getEasing((_c = options.easing) !== null && _c !== void 0 ? _c : 'easeOut');
        var repeat = (_d = options.repeat) !== null && _d !== void 0 ? _d : 0;
        var reverse = (_e = options.reverse) !== null && _e !== void 0 ? _e : false;
        var loop = (_f = options.loop) !== null && _f !== void 0 ? _f : false;
        var from = {};
        var to = {};
        var skipKeys = new Set([
            'duration',
            'delay',
            'easing',
            'repeat',
            'reverse',
            'loop',
            'onStart',
            'onUpdate',
            'onComplete',
            'motionPath',
            'morphTo',
        ]);
        for (var key in options) {
            if (skipKeys.has(key))
                continue;
            var val = options[key];
            if (typeof val === 'number' || typeof val === 'string') {
                from[key] = target[key];
                to[key] = val;
            }
        }
        var motionPathD = resolveMotionPath(options.motionPath);
        var pathLength = motionPathD ? getPathLength(motionPathD) : 0;
        var morphTo = options.morphTo;
        var morphFrom = morphTo && typeof target.d === 'string' ? target.d : null;
        var state = {
            id: nextId++,
            target: target,
            from: from,
            to: to,
            startTime: now(),
            duration: duration,
            delay: delay,
            easing: easing,
            repeat: repeat,
            reverse: reverse,
            loop: loop,
            iteration: 0,
            reversed: false,
            motionPathD: motionPathD,
            pathLength: pathLength,
            morphFrom: morphFrom,
            morphTo: morphTo !== null && morphTo !== void 0 ? morphTo : null,
            onStart: options.onStart,
            onUpdate: options.onUpdate,
            onComplete: options.onComplete,
            frameId: 0,
            active: true,
            pausedElapsed: null,
        };
        (_g = options.onStart) === null || _g === void 0 ? void 0 : _g.call(options);
        activeAnimations.push(state);
        scheduleTick();
        return {
            stop: function () {
                state.active = false;
                state.pausedElapsed = null;
                var idx = activeAnimations.indexOf(state);
                if (idx >= 0)
                    activeAnimations.splice(idx, 1);
                if (!activeAnimations.some(function (a) { return a.active; }))
                    cancelPendingTick();
            },
            pause: function () {
                if (!state.active)
                    return;
                state.pausedElapsed = Math.max(0, now() - state.startTime);
                state.active = false;
                if (!activeAnimations.some(function (a) { return a.active; }))
                    cancelPendingTick();
            },
            resume: function () {
                var _a;
                if (state.active)
                    return;
                var idx = activeAnimations.indexOf(state);
                if (idx < 0)
                    return; // stopped
                var elapsed = (_a = state.pausedElapsed) !== null && _a !== void 0 ? _a : 0;
                state.startTime = now() - elapsed;
                state.pausedElapsed = null;
                state.active = true;
                scheduleTick();
            },
        };
    };
    AnimationEngine.stopAll = function () {
        activeAnimations.length = 0;
        cancelPendingTick();
    };
    AnimationEngine.onFrame = null;
    return AnimationEngine;
}());
export { AnimationEngine };
export function animate(target, options) {
    return AnimationEngine.animate(target, options);
}
